// Composable compartilhado do Kanban: dado uma conversa, diz em que funil/etapa
// ela está (badge na lista + box na lateral). Reusa a MESMA fonte do KanbanView:
// os Atributos Personalizados de conversa do tipo lista = funis; a cor da etapa
// vive no attribute_description (marcador ` ::kbcolors:: `), compartilhada com a
// equipe. NÃO faz chamada de API — lê do store de atributos já carregado.
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';

// Mesma paleta do KanbanView (classes literais p/ o Tailwind gerar).
const STAGE_COLORS = [
  { key: 'slate', dot: 'bg-n-slate-9', accent: 'text-n-slate-11' },
  { key: 'teal', dot: 'bg-n-teal-9', accent: 'text-n-teal-11' },
  { key: 'blue', dot: 'bg-n-blue-9', accent: 'text-n-blue-11' },
  { key: 'iris', dot: 'bg-n-iris-9', accent: 'text-n-iris-11' },
  { key: 'violet', dot: 'bg-n-violet-9', accent: 'text-n-violet-9' },
  { key: 'amber', dot: 'bg-n-amber-9', accent: 'text-n-amber-11' },
  { key: 'ruby', dot: 'bg-n-ruby-9', accent: 'text-n-ruby-11' },
];
const DEFAULT_COLOR = 'slate';
const findColor = key => STAGE_COLORS.find(c => c.key === key) || STAGE_COLORS[0];

const COLOR_MARKER = ' ::kbcolors:: ';
const parseColorsFromDescription = desc => {
  if (!desc) return {};
  const idx = String(desc).indexOf(COLOR_MARKER);
  if (idx === -1) return {};
  try {
    return JSON.parse(String(desc).slice(idx + COLOR_MARKER.length)) || {};
  } catch (e) {
    return {};
  }
};

export function useKanbanFunnels() {
  const conversationAttributes = useMapGetter(
    'attributes/getConversationAttributes'
  );

  // Funis = atributos de conversa do tipo lista. O store getter entrega os campos
  // em camelCase (attributeDisplayType/attributeKey/...); a API crua usa snake_case.
  // Lemos camelCase com fallback snake pra funcionar nos dois casos.
  const funnels = computed(() =>
    (conversationAttributes.value || [])
      .filter(a => (a.attributeDisplayType ?? a.attribute_display_type) === 'list')
      .map(a => ({
        key: a.attributeKey ?? a.attribute_key,
        name: a.attributeDisplayName ?? a.attribute_display_name,
        colorMap: parseColorsFromDescription(
          a.attributeDescription ?? a.attribute_description
        ),
      }))
  );

  // Para uma conversa, retorna os funis em que ela tem etapa definida.
  const funnelsForConversation = conversation => {
    const ca = conversation?.custom_attributes || {};
    return funnels.value
      .filter(f => ca[f.key] !== undefined && ca[f.key] !== null && ca[f.key] !== '')
      .map(f => {
        const stage = String(ca[f.key]);
        const color = findColor(f.colorMap[stage] || DEFAULT_COLOR);
        return {
          funnelKey: f.key,
          funnelName: f.name,
          stage,
          dotClass: color.dot,
          accentClass: color.accent,
        };
      });
  };

  return { funnels, funnelsForConversation };
}
